import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Download,
  FileText,
  Star,
  Stethoscope,
  Video,
  Printer,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import ReviewModal from "../components/ReviewModal";
import { api, formatApiErrorDetail } from "../lib/api";

const FALLBACK_DOCTOR_IMG =
  "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=400&q=80";

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  });
}

const STATUS_BADGES = {
  upcoming: "bg-accent text-accent-foreground",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
  pending_payment: "bg-amber-50 text-amber-700",
};

export default function PatientAppointmentDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const justBooked = params.get("just_booked") === "1";

  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/appointments/${id}`);
      setAppt(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openInvoice = async () => {
    if (!appt?.invoice?.id) return;
    try {
      const { data } = await api.get(`/invoices/${appt.invoice.id}`);
      const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Invoice ${data.id}</title>
<style>
body { font-family: system-ui, -apple-system, sans-serif; padding: 40px; max-width: 720px; margin: 0 auto; color: #0a2333; }
h1 { font-size: 22px; margin: 0 0 4px; }
.brand { color: #0EA5A4; font-weight: 700; letter-spacing: -0.02em; }
table { width: 100%; border-collapse: collapse; margin-top: 24px; }
td { padding: 10px 0; border-bottom: 1px solid #e5e7eb; font-size: 14px; }
td.amount { text-align: right; font-variant-numeric: tabular-nums; }
.muted { color: #6b7280; font-size: 13px; }
.total { font-size: 18px; font-weight: 700; }
.box { background: #f6fbfb; border: 1px solid #d6ecec; border-radius: 12px; padding: 16px; margin-top: 16px; }
</style></head><body>
<div class="brand">Healthink</div>
<h1>Invoice ${data.id}</h1>
<div class="muted">Issued ${fmt(data.created_at)}</div>
<div class="box">
  <div><strong>${data.doctor?.name || ""}</strong> — ${data.doctor?.category || ""}</div>
  <div class="muted">Appointment ${data.appointment?.id} · ${data.appointment?.mode?.toUpperCase()} · ${fmt(data.appointment?.scheduled_at)}</div>
</div>
<table>
  <tr><td>Subtotal</td><td class="amount">₹${data.amount.toFixed(2)}</td></tr>
  <tr><td>GST (18%)</td><td class="amount">₹${data.gst_amount.toFixed(2)}</td></tr>
  <tr><td class="total">Total paid</td><td class="amount total">₹${data.total.toFixed(2)}</td></tr>
</table>
<p class="muted">Payment ID: ${data.payment?.razorpay_payment_id || "—"} · Method: ${data.payment?.method || "—"} · Status: ${data.payment?.status || "—"}</p>
</body></html>`;
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="appt-detail-page"
        className="flex-1 px-6 lg:px-10 py-6 lg:py-8 max-w-3xl w-full mx-auto space-y-6"
      >
        <Link
          to="/patient/appointments"
          data-testid="appt-detail-back-link"
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" /> Back to appointments
        </Link>

        {justBooked && (
          <div
            data-testid="appt-just-booked-banner"
            className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-2xl p-4 flex items-start gap-3"
          >
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Booking confirmed</div>
              <div className="text-sm opacity-90">
                We've emailed you the details. Your appointment ID is{" "}
                <span className="font-mono font-medium">{appt?.id}</span>.
              </div>
            </div>
          </div>
        )}

        {loading || !appt ? (
          <div className="h-64 bg-muted rounded-3xl animate-pulse" />
        ) : (
          <>
            <section className="bg-white border border-border rounded-3xl p-6 sm:p-8 shadow-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Appointment
                  </div>
                  <div className="font-mono text-base mt-0.5" data-testid="appt-id-display">
                    {appt.id}
                  </div>
                </div>
                <span
                  className={
                    "text-[11px] uppercase tracking-wide font-semibold px-2.5 py-1 rounded-full " +
                    (STATUS_BADGES[appt.status] || "bg-muted text-muted-foreground")
                  }
                >
                  {appt.status}
                </span>
              </div>

              <div className="flex items-center gap-4 mt-6">
                <img
                  src={appt.doctor?.image_url || FALLBACK_DOCTOR_IMG}
                  alt={appt.doctor?.name}
                  className="h-16 w-16 rounded-xl object-cover"
                />
                <div>
                  <div className="font-semibold text-lg">{appt.doctor?.name}</div>
                  <div className="text-sm text-muted-foreground">{appt.doctor?.category}</div>
                  <div className="text-xs text-muted-foreground">{appt.doctor?.qualification}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-border">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">When</div>
                  <div className="font-medium mt-1 inline-flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    {fmt(appt.scheduled_at)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Mode</div>
                  <div className="font-medium mt-1 inline-flex items-center gap-1.5 capitalize">
                    {appt.mode === "video" ? (
                      <Video className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Stethoscope className="h-4 w-4 text-muted-foreground" />
                    )}
                    {appt.mode}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Fee</div>
                  <div className="font-medium mt-1">₹{appt.fee}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">
                    Payment ID
                  </div>
                  <div className="font-mono text-xs mt-1 truncate">
                    {appt.payment?.razorpay_payment_id || "—"}
                  </div>
                </div>
              </div>
            </section>

            {appt.invoice && (
              <section
                data-testid="appt-invoice-section"
                className="bg-white border border-border rounded-3xl p-6 sm:p-8 shadow-sm"
              >
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-lg font-semibold tracking-tight inline-flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" /> Invoice
                    </h2>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {appt.invoice.id} · {fmt(appt.invoice.created_at)}
                    </div>
                  </div>
                  <Button
                    data-testid="appt-download-invoice-btn"
                    variant="outline"
                    onClick={openInvoice}
                    className="rounded-full"
                  >
                    <Download className="h-4 w-4 mr-2" /> Download
                  </Button>
                </div>

                <div className="mt-5 border-t border-border">
                  <div className="flex justify-between py-3 border-b border-border text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="tabular-nums">₹{appt.invoice.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-border text-sm">
                    <span className="text-muted-foreground">GST (18%)</span>
                    <span className="tabular-nums">₹{appt.invoice.gst_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3 text-base font-semibold">
                    <span>Total paid</span>
                    <span className="tabular-nums">₹{appt.invoice.total.toFixed(2)}</span>
                  </div>
                </div>
              </section>
            )}

            {appt.review && (
              <section
                data-testid="appt-review-section"
                className="bg-white border border-border rounded-3xl p-6 sm:p-8 shadow-sm"
              >
                <h2 className="text-lg font-semibold tracking-tight inline-flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-500" /> Your review
                </h2>
                <div className="mt-3 flex items-center gap-1" data-testid="appt-review-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={
                        "h-5 w-5 " +
                        (n <= appt.review.rating
                          ? "text-amber-400 fill-amber-400"
                          : "text-muted-foreground/30")
                      }
                    />
                  ))}
                  <span className="ml-2 text-sm text-muted-foreground">
                    {appt.review.rating}/5
                  </span>
                </div>
                {appt.review.comment && (
                  <p className="mt-3 text-sm text-foreground/90">{appt.review.comment}</p>
                )}
              </section>
            )}

            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => navigate("/patient/appointments")}
                className="rounded-full"
              >
                All appointments
              </Button>
              {(appt.status === "upcoming" || appt.status === "in_progress") && appt.mode === "video" && (
                <Button
                  data-testid="appt-join-video-btn"
                  onClick={() => navigate(`/patient/consult/${appt.id}`)}
                  className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground gap-1.5"
                >
                  <Video className="h-4 w-4" /> Join Video Call
                </Button>
              )}
              {appt.status === "completed" && !appt.review && (
                <Button
                  data-testid="appt-rate-btn"
                  variant="outline"
                  onClick={() => setReviewOpen(true)}
                  className="rounded-full"
                >
                  <Star className="h-4 w-4 mr-2" /> Rate consultation
                </Button>
              )}
              {appt.status === "completed" && (
                <Button
                  data-testid="appt-view-prescription-btn"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/patient/appointments/${appt.id}/prescription`);
                      if (!data || !data.id) {
                        toast.info("No prescription was added for this appointment.");
                        return;
                      }
                      const token = localStorage.getItem("telemed_token") || localStorage.getItem("healthink_token") || localStorage.getItem("token") || "";
                      const backendBase = (api.defaults.baseURL || "").replace(/\/api$/, "");
                      window.open(`${backendBase}/api/prescriptions/${data.id}/pdf${token ? `?token=${encodeURIComponent(token)}` : ''}`, "_blank");
                    } catch (err) {
                      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                    }
                  }}
                  className="rounded-full gap-1.5"
                >
                  <Printer className="h-4 w-4 text-primary" /> Print / Download Rx
                </Button>
              )}
              <Button
                onClick={() => navigate("/patient/doctors")}
                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
              >
                Book another
              </Button>
            </div>
          </>
        )}
      </main>
      {appt && (
        <ReviewModal
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          appointmentId={appt.id}
          doctorName={appt.doctor?.name}
          onSubmitted={() => load()}
        />
      )}
    </div>
  );
}
